import RemoteSelect from 'common/components/RemoteSelect/RemoteSelect';
import { Address } from 'types/Address';
import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';

const defaultOptions = {
  fields: ['id', 'CP', 'localisation'],
  defaultSorts: ['INSEE'],
  pageSize: 10,
};

function processOptions(options: Address[]) {
  if (!Array.isArray(options)) {
    options = [options];
  }
  return options.map((address) => ({
    value: address.id,
    label: `${address.CP} - ${address.localisation}`,
    CP: address.CP,
    localisation: address.localisation,
  }));
}

function AddressSelect({ value, onChange, ...props }: any) {
  const [searchText, setSearchText] = useState('');
  const [debouncedText, setDebouncedText] = useState('');

  // Create a debounced function that will be cancelled if unmounted
  const debouncedSetText = useMemo(
    () =>
      debounce(
        (text: string) => {
          setDebouncedText(text);
        },
        200,
        { leading: false, trailing: true }
      ),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetText.cancel();
    };
  }, [debouncedSetText]);

  const handleInputChange = useCallback(
    (inputValue: string) => {
      setSearchText(inputValue); // Immediate update for UI
      debouncedSetText(inputValue); // Debounced update for API calls
    },
    [debouncedSetText]
  );

  const filters = useMemo(() => {
    // If we have a value and no search text, filter for the initial value
    if (value && !debouncedText) {
      return [
        {
          name: 'id',
          value,
        },
      ];
    }

    // Otherwise filter based on search
    if (!debouncedText) return [];

    // This will create:
    // filters[$or][0][CP][$startsWithi]=value
    // filters[$or][1][localisation][$startsWithi]=value
    return [
      {
        name: '$or][0][CP][$startsWithi',
        value: debouncedText,
      },
      {
        name: '$or][1][localisation][$startsWithi',
        value: debouncedText,
      },
    ];
  }, [debouncedText, value]);

  const handleMenuOpen = useCallback(() => {
    setSearchText('');
    setDebouncedText('');
  }, []);

  return (
    <RemoteSelect
      url="addresses"
      processOptions={processOptions}
      filters={filters}
      sort="INSEE"
      pagination={{
        pageSize: defaultOptions.pageSize,
      }}
      populate={defaultOptions.fields}
      onInputChange={handleInputChange}
      inputValue={searchText}
      value={value}
      onChange={onChange}
      onMenuOpen={handleMenuOpen}
      {...props}
    />
  );
}

export default AddressSelect;
